/**
 * Weekly digest — review the Saturday reader digest per masthead (email M4b).
 *
 * Accountable mode: every item shows why it is there (its rank, readers
 * in the week, LinkedIn engagement) and any reader-facing correction made to
 * it, with the time the issue was built. "Build / rebuild" re-reads every
 * article (current title, current excerpt, still published), so corrections
 * are picked up. This page sends nothing: test sends, approval and the
 * Saturday 09:00 Sydney schedule arrive in M4c, and the first issue needs
 * Steve's explicit go.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Eye, EyeOff, RefreshCw, AlertTriangle, Hammer, Send, CheckCircle2, XCircle, Power } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.js';
import { TableScroll } from '../components/ui/table-scroll.js';
import {
  approveDigest,
  buildDigest,
  cancelDigest,
  getDigestIssue,
  getDigestSettings,
  listDigestIssues,
  sendDigestTest,
  setDigestSendEnabled,
  type DigestIssue,
  type DigestItemEvidence,
} from '../lib/api/notifications.js';
import { SITE_LABEL } from '../lib/analyticsShape.js';

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-AU', {
    timeZone: 'Australia/Sydney', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

function prevDay(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return new Date(d.getTime() - 86_400_000).toISOString().slice(0, 10);
}

export default function DigestPage() {
  const qc = useQueryClient();
  const [sendDate, setSendDate] = useState<string | null>(null);
  const list = useQuery({
    queryKey: ['digest-issues', sendDate],
    queryFn: () => listDigestIssues(sendDate ? prevDay(sendDate) : undefined),
  });
  const effectiveSend = sendDate ?? list.data?.next_send_date ?? null;
  const issues = useMemo(() => {
    const all = list.data?.items ?? [];
    if (!effectiveSend) return all;
    return all.filter((i) => i.period_end === prevDay(effectiveSend));
  }, [list.data, effectiveSend]);

  const build = useMutation({
    mutationFn: () => buildDigest(effectiveSend ?? undefined),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['digest-issues'] }),
  });

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-sm text-grey-500 mb-1">
            <Link to="/notifications/subscribers" className="inline-flex items-center gap-1 hover:text-grey-700">
              <ArrowLeft className="h-3.5 w-3.5" />
              Subscribers
            </Link>
          </div>
          <h1 className="text-2xl font-semibold text-grey-900">Weekly digest</h1>
          <p className="text-sm text-grey-600 mt-1 max-w-3xl">
            The Saturday email to each masthead&apos;s subscribers: the week&apos;s five most-read articles,
            promoted coverage (one per coverage customer not already in the top five) and the top story from
            each other masthead. Ranked by readers (page views minus the suspected crawler), then LinkedIn engagement, then recency. Titles and
            excerpts are the articles&apos; own; the email adds no new wording.
          </p>
          <SendSwitch />
        </div>
        <button
          onClick={() => list.refetch()}
          className="flex-shrink-0 inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-grey-300 rounded hover:bg-grey-50"
        >
          <RefreshCw className={`h-4 w-4 ${list.isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </header>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <label className="text-sm text-grey-700">
          Send date (Saturday){' '}
          <input
            type="date"
            value={effectiveSend ?? ''}
            onChange={(e) => setSendDate(e.target.value || null)}
            className="input ml-1"
          />
        </label>
        <button
          onClick={() => build.mutate()}
          disabled={build.isPending || !effectiveSend}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
          title="Re-reads every article: current title, current excerpt, still published"
        >
          <Hammer className="h-4 w-4" />
          {build.isPending ? 'Building…' : issues.length ? 'Rebuild drafts' : 'Build drafts'}
        </button>
        {build.isError && (
          <span className="text-sm text-danger-600 inline-flex items-center gap-1">
            <AlertTriangle className="h-4 w-4" /> {(build.error as Error)?.message ?? 'Build failed'}
          </span>
        )}
      </div>

      {list.isLoading && <p className="text-sm text-grey-500">Loading…</p>}
      {!list.isLoading && issues.length === 0 && (
        <p className="text-sm text-grey-500">No draft issues for this Saturday yet. Build them to review.</p>
      )}

      <div className="flex flex-col gap-6">
        {issues.map((i) => <IssueCard key={i.issue_id} issue={i} />)}
      </div>
    </div>
  );
}

function apiError(e: unknown): string {
  const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (d && typeof d === 'object') {
    const o = d as { error?: string; detail?: unknown };
    const det = Array.isArray(o.detail) ? o.detail.join('; ') : o.detail ? JSON.stringify(o.detail) : '';
    return [o.error, det].filter(Boolean).join(': ');
  }
  return (e as Error)?.message ?? 'failed';
}

function SendSwitch() {
  const qc = useQueryClient();
  const { hasRole } = useAuth();
  const settings = useQuery({ queryKey: ['digest-settings'], queryFn: getDigestSettings });
  const toggle = useMutation({
    mutationFn: (enabled: boolean) => setDigestSendEnabled(enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['digest-settings'] }),
  });
  const on = settings.data?.send_enabled === true;
  return (
    <div className={`mt-3 inline-flex flex-wrap items-center gap-3 rounded border px-3 py-2 text-xs ${on ? 'border-success-200 bg-success-50 text-success-700' : 'border-warning-200 bg-warning-50 text-warning-700'}`}>
      <Power className="h-4 w-4" />
      <span>
        {on
          ? 'Sending is ON: approved issues go out at Saturday 09:00 Sydney (within 10 minutes), after a final re-read.'
          : 'Sending is OFF: approved issues will not go out. The first issue needs Steve’s explicit go.'}
        {settings.data?.updated_by && <> Last set by {settings.data.updated_by}.</>}
      </span>
      {hasRole('super_admin') && (
        <button
          onClick={() => toggle.mutate(!on)}
          disabled={toggle.isPending || settings.isLoading}
          className="rounded border border-current px-2 py-0.5 font-medium hover:bg-white/50 disabled:opacity-50"
        >
          {on ? 'Turn sending off' : 'Turn sending on'}
        </button>
      )}
    </div>
  );
}

function IssueCard({ issue }: { issue: DigestIssue }) {
  const qc = useQueryClient();
  const [preview, setPreview] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['digest-issues'] });
  const test = useMutation({
    mutationFn: () => sendDigestTest(issue.issue_id),
    onSuccess: (r) => { setNote(`Test sent to you.${r.changes.length ? ` Note: ${r.changes.length} change(s) since build. Rebuild before approving.` : ''}`); refresh(); },
    onError: (e) => setNote(`Test failed: ${apiError(e)}`),
  });
  const approve = useMutation({
    mutationFn: () => approveDigest(issue.issue_id, issue.built_at ?? ''),
    onSuccess: (r) => { setNote(`Approved for ${fmtDateTime(r.scheduled_for)} (Sydney): ${r.expected_recipients} recipients (Mailjet ${r.mailjet_active}).`); refresh(); },
    onError: (e) => setNote(`Not approved: ${apiError(e)}`),
  });
  const cancel = useMutation({
    mutationFn: () => cancelDigest(issue.issue_id),
    onSuccess: () => { setNote('Approval cancelled; back to draft.'); refresh(); },
    onError: (e) => setNote(`Cancel failed: ${apiError(e)}`),
  });
  const detail = useQuery({
    queryKey: ['digest-issue', issue.issue_id, issue.built_at],
    queryFn: () => getDigestIssue(issue.issue_id),
    enabled: preview,
  });
  const s = issue.content?.sections;
  const rows: Array<{ section: string; it: DigestItemEvidence }> = [
    ...(s?.top ?? []).map((it) => ({ section: 'Most read', it })),
    ...(s?.promoted ?? []).map((it) => ({ section: 'Coverage', it })),
    ...(s?.trending ?? []).map((it) => ({ section: 'Network', it })),
  ];
  const builtAt = issue.built_at ? Date.parse(issue.built_at) : null;

  return (
    <section className="bg-surface rounded-xl border border-grey-200 p-4 flex flex-col gap-3">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold text-grey-900">{SITE_LABEL[issue.target_name] ?? issue.target_name}</h2>
          <p className="text-sm text-grey-800">{issue.subject ?? '—'}</p>
          <p className="text-xs text-grey-500 mt-0.5">
            {issue.content?.period_label} · status <strong>{issue.status}</strong> · built {fmtDateTime(issue.built_at)}
            {issue.created_by && <> by {issue.created_by}</>} · {issue.expected_recipients ?? '—'} expected recipients
            (Mailjet list {issue.content?.list_id}, includes internal) · {issue.content?.candidates_considered ?? 0} articles considered
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => test.mutate()}
          disabled={test.isPending}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-grey-300 rounded hover:bg-grey-50 disabled:opacity-50"
          title="Sends this issue to your own address only"
        >
          <Send className="h-3.5 w-3.5" /> {test.isPending ? 'Sending…' : 'Send test to me'}
        </button>
        {issue.status === 'draft' && (
          <button
            onClick={() => approve.mutate()}
            disabled={approve.isPending || !issue.built_at}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
            title="Approves exactly this build for Saturday 09:00 Sydney. It is re-read before sending; any change returns it to draft."
          >
            <CheckCircle2 className="h-3.5 w-3.5" /> {approve.isPending ? 'Approving…' : 'Approve for Saturday 09:00'}
          </button>
        )}
        {issue.status === 'approved' && (
          <button
            onClick={() => cancel.mutate()}
            disabled={cancel.isPending}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-danger-300 text-danger-700 rounded hover:bg-danger-50 disabled:opacity-50"
          >
            <XCircle className="h-3.5 w-3.5" /> Cancel approval
          </button>
        )}
        <button
          onClick={() => setPreview((p) => !p)}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-grey-300 rounded hover:bg-grey-50"
        >
          {preview ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          {preview ? 'Hide email' : 'Preview email'}
        </button>
        </div>
      </header>

      {(issue.approved_by || issue.last_test_sent_at || issue.error_message || note) && (
        <div className="text-xs flex flex-col gap-0.5">
          {issue.approved_by && <span className="text-grey-600">Approved by {issue.approved_by}; sends {fmtDateTime(issue.scheduled_for)} (Sydney), after a final re-read.</span>}
          {issue.last_test_sent_at && <span className="text-grey-500">Last test {fmtDateTime(issue.last_test_sent_at)} to {issue.last_test_sent_to}.</span>}
          {issue.error_message && <span className="text-danger-600">{issue.error_message}</span>}
          {note && <span className="text-grey-800">{note}</span>}
        </div>
      )}

      <TableScroll>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-grey-500">
              <th className="px-2 py-1 font-medium">Section</th>
              <th className="px-2 py-1 font-medium">Rank</th>
              <th className="px-2 py-1 font-medium">Article</th>
              <th className="px-2 py-1 font-medium text-right">Readers</th>
              <th className="px-2 py-1 font-medium text-right">LinkedIn</th>
              <th className="px-2 py-1 font-medium">Notes</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ section, it }) => {
              const correctedAfterBuild = it.corrected_at && builtAt !== null && Date.parse(it.corrected_at) > builtAt;
              return (
                <tr key={`${section}-${it.article_id}`} className="border-t border-grey-100 align-top">
                  <td className="px-2 py-1 whitespace-nowrap">{section}</td>
                  <td className="px-2 py-1">#{it.rank}{section === 'Network' && <span className="text-grey-500"> {SITE_LABEL[it.masthead] ?? it.masthead}</span>}</td>
                  <td className="px-2 py-1">
                    <a href={it.url} target="_blank" rel="noreferrer" className="text-primary-700 hover:underline">{it.title}</a>
                    <div className="text-grey-500 mt-0.5">{it.excerpt}</div>
                  </td>
                  <td className="px-2 py-1 text-right">{it.views}</td>
                  <td className="px-2 py-1 text-right">{it.linkedin_engagement == null ? '—' : it.linkedin_engagement.toFixed(3)}</td>
                  <td className="px-2 py-1">
                    {it.coverage && <span className="inline-block mr-1 rounded border border-primary-300 px-1 text-primary-700">Coverage</span>}
                    {it.corrected_at && (
                      <span className={correctedAfterBuild ? 'text-danger-600 font-medium' : 'text-grey-500'}>
                        corrected {fmtDateTime(it.corrected_at)}{correctedAfterBuild ? ', after this build: rebuild' : ''}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableScroll>

      {preview && (
        detail.isLoading ? <p className="text-sm text-grey-500">Loading preview…</p> : (
          <iframe
            title={`${issue.target_name} digest preview`}
            sandbox=""
            srcDoc={detail.data?.rendered_html ?? ''}
            className="w-full h-[720px] rounded border border-grey-200 bg-white"
          />
        )
      )}
    </section>
  );
}
